import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Send, ShieldCheck, Users } from "lucide-react";
import {
  fetchDistributorProfile,
  loginMemberByEmailCode,
  sendMemberEmailCode,
  type ApiError,
} from "@/api/member";
import { ensureDistributorAccessToken, saveDistributorSession } from "@/utils/distributorSession";

export default function DistributorLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    ensureDistributorAccessToken()
      .then(async (token) => {
        if (!token) {
          return;
        }
        await fetchDistributorProfile(token);
        navigate("/distributor/dashboard", { replace: true });
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (countdown <= 0) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const validateEmail = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSendCode = async () => {
    setError("");
    if (!validateEmail()) {
      setError("请输入有效的邮箱地址");
      return;
    }
    setSending(true);
    try {
      await sendMemberEmailCode(email.trim());
      setCountdown(60);
    } catch (err) {
      setError((err as ApiError).message || "验证码发送失败");
    } finally {
      setSending(false);
    }
  };

  const handleLogin = async () => {
    setError("");
    if (!validateEmail()) {
      setError("请输入有效的邮箱地址");
      return;
    }
    if (!code.trim()) {
      setError("请输入邮箱验证码");
      return;
    }
    setLoggingIn(true);
    try {
      const loginResult = await loginMemberByEmailCode(email.trim(), code.trim());
      const session = saveDistributorSession(loginResult);
      await fetchDistributorProfile(session.accessToken);
      navigate("/distributor/dashboard", { replace: true });
    } catch (err) {
      setError((err as ApiError).message || "登录失败");
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-800 text-white flex flex-col items-center justify-center px-4 py-8">
      <Link
        to="/"
        className="fixed left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-dark-500 border border-dark-300 text-white/60 hover:text-white"
        aria-label="返回首页"
      >
        <ArrowLeft size={20} />
      </Link>

      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-dark-300 bg-dark-500">
          <Users className="text-mint-400" size={30} />
        </div>
        <h1 className="text-2xl font-bold">分销商登录</h1>
        <p className="mt-2 text-sm text-white/45">黄果TV推广联盟</p>
      </div>

      <div className="w-full max-w-md rounded-2xl border border-dark-300 bg-dark-500 p-6 shadow-2xl">
        <div className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-sm text-white/65">邮箱</span>
            <div className="flex items-center gap-2 rounded-button border border-dark-300 bg-dark-700 px-3 focus-within:border-mint-400">
              <Mail size={18} className="text-white/45" />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="输入分销商邮箱"
                className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-white/25"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm text-white/65">验证码</span>
            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="输入验证码"
                className="h-12 min-w-0 flex-1 rounded-button border border-dark-300 bg-dark-700 px-4 text-sm outline-none placeholder:text-white/25 focus:border-mint-400"
              />
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sending || countdown > 0}
                className="inline-flex h-12 min-w-28 items-center justify-center gap-2 rounded-button border border-dark-300 bg-dark-400 px-4 text-sm font-medium text-white hover:bg-dark-300 disabled:cursor-not-allowed disabled:opacity-55"
              >
                <Send size={16} />
                {countdown > 0 ? `${countdown}s` : sending ? "发送中" : "验证码"}
              </button>
            </div>
          </label>

          {error && <p className="rounded-button bg-red-500/10 px-3 py-2 text-center text-sm text-red-300">{error}</p>}

          <button
            type="button"
            onClick={handleLogin}
            disabled={loggingIn}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-button bg-[#F97316] font-bold text-dark-900 hover:bg-[#F97316]/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ShieldCheck size={18} />
            {loggingIn ? "登录中..." : "登录分销后台"}
          </button>
        </div>

        <p className="mt-5 border-t border-dark-300 pt-4 text-center text-xs leading-5 text-white/35">
          当前账号需要拥有分销商角色。未开通时，请联系平台管理员。
        </p>
      </div>
    </div>
  );
}
